<script setup lang="ts">
  import type { Recipe } from '@/types/Recipe'

  defineProps<{ recipe: Recipe; isPinned: boolean }>()
  const emit = defineEmits<{ (event: 'toggle-pinned'): void }>()
</script>

<template>
  <article class="recipeCard">
    <img v-if="recipe.media?.length" :src="recipe.media[0]" :alt="recipe.title as string" class="recipeCardImage" />
    <span v-else class="recipeCardImage recipeCardImagePlaceholderWrapper">
      <img src="/logo-192.png" :alt="recipe.title as string" class="recipeCardImagePlaceholder" />
    </span>
    <div class="recipeCardBody">
      <span class="recipeCardBodyTitle"> {{ recipe.title }} {{ recipe.subtitle || '' }} </span>

      <div v-if="recipe.totalTime || recipe.serves" class="recipeCardBodyInfo">
        <p v-if="recipe.totalTime" class="recipeCardBodyInfoStat">
          <i class="fad fa-fw fa-cauldron"></i>
          <span>{{ recipe.totalTime }}</span>
        </p>
        <p v-if="recipe.serves" class="recipeCardBodyInfoStat">
          <i class="fad fa-fw fa-utensils"></i>
          <span>Serves {{ recipe.serves }}</span>
        </p>
      </div>
    </div>

    <button
      class="place-items-center hover:shadow-md top-2 right-2 hover:bg-opacity-100 aspect-square group absolute grid w-8 text-xs transition bg-white bg-opacity-75 rounded shadow-sm"
      :class="{ 'bg-opacity-90': isPinned }"
      @click.stop.prevent="emit('toggle-pinned')"
    >
      <i
        class="far fa-fw fa-thumbtack text-juss-purple-300 group-hover:text-juss-purple-500 transition"
        :class="{ 'fas text-juss-purple-400 group-hover:text-juss-purple-400': isPinned }"
      />
    </button>
  </article>
</template>

<style lang="sass" scoped>
  .recipeCard
    border: 1px solid theme('colors.slate.200')
    width: 240px
    border-radius: 8px
    position: relative
    transition: 150ms ease-in-out
    &:hover
      box-shadow: 0 4px 6px -1px #f1f5f9 , 0 2px 4px -2px #f1f5f9
      .recipeCardBodyTitle
        color: theme('colors.slate.800')

    &Image
      width: 100%
      height: 128px
      aspect-ratio: auto
      object-fit: cover
      object-position: center
      border-top-left-radius: 7px
      border-top-right-radius: 7px
      &PlaceholderWrapper
        display: grid
        place-items: center
        background-color: theme('colors.purple.200')
      &Placeholder
        height: 64px
        aspect-ratio: auto

    &Body
      border-top: 1px solid theme('colors.slate.200')
      padding: 8px
      &Title
        font-weight: bold
        color: theme('colors.slate.500')
        transition: 150ms ease-in-out
      &Info
        display: flex
        gap: 8px
        margin-top: 8px
        color: theme('colors.slate.500')
        &Stat
          display: flex
          align-items: center
          gap: 4px
          font-size: 12px
          line-height: 16px
</style>
